import React from "react";
import { LINKS } from "../data/site";
import { ArrowUpRight, Instagram, Facebook, Linkedin, Mail } from "lucide-react";

/**
 * Founder / Next Video / Let's Connect — bottom three sections.
 * Typography (Global Assets):
 *   • "Founded by," / "Next Video" / "Let's Connect" → Clash Display Medium,
 *     gradient (matches Featured Projects / Creators / Our Process gradient)
 *   • "Sohan Bin Ashik"                → Clash Display Regular (400)
 *   • "Founder & Creative Director"    → Karvest Trial Regular
 *   • "Clickory is a YouTube …"        → Karvest Trial Regular
 *   • "We help creators improve …"     → Karvest Trial Regular
 *   • "Let's Package Your"             → Karvest Trial Regular
 *   • "Ready to package your next
 *     video? Let's talk."              → Karvest Trial Regular
 */

function XIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.53 3H21l-7.4 8.46L22 21h-6.86l-4.78-6.24L4.9 21H1.42l7.92-9.05L2 3h7l4.32 5.72L17.53 3Zm-1.2 16h1.9L7.83 5H5.83l10.5 14Z" />
    </svg>
  );
}

const HEADING_GRADIENT =
  "linear-gradient(135deg,#EDE9FE 0%,#C4B5FD 35%,#8B5CF6 70%,#7C3AED 100%)";

export default function Founder() {
  return (
    <section id="founder" className="relative py-6 md:py-14">
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        {/* Section heading */}
        <div className="text-center reveal">
          <h2
            className="text-transparent bg-clip-text"
            style={{
              fontFamily: "'Clash Display',sans-serif",
              fontWeight: 500,
              fontSize: "clamp(46px, 8vw, 92px)",
              lineHeight: 0.95,
              letterSpacing: "-0.02em",
              backgroundImage: HEADING_GRADIENT,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              margin: 0,
            }}
          >
            Founded by,
          </h2>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-2 items-center gap-4 md:gap-12">
          {/* Text column — LEFT on both mobile and desktop */}
          <div className="reveal text-left">
            <div
              className="text-white"
              style={{
                fontFamily: "'Clash Display',sans-serif",
                fontWeight: 400,
                fontSize: "clamp(28px, 6vw, 80px)",
                lineHeight: 0.95,
                letterSpacing: "-0.02em",
              }}
            >
              Sohan
              <br />
              Bin Ashik
            </div>
            <span
              className="inline-block mt-3 md:mt-4 px-3 md:px-4 py-1 md:py-1.5 rounded-md text-white"
              style={{
                background: "rgba(79,70,229,0.35)",
                border: "1px solid rgba(255,255,255,0.1)",
                fontFamily: "'Karvest',sans-serif",
                fontWeight: 400,
                fontSize: "clamp(10px, 1vw, 15px)",
                letterSpacing: "0.005em",
              }}
            >
              Founder &amp; Creative Director
            </span>
            <p
              className="mt-4 md:mt-6 max-w-md"
              style={{
                fontFamily: "'Karvest',sans-serif",
                fontWeight: 400,
                fontSize: "clamp(11px, 1vw, 16px)",
                lineHeight: 1.5,
                letterSpacing: "0.005em",
                color: "rgba(255,255,255,0.88)",
              }}
            >
              Clickory is a YouTube Packaging Agency founded by{" "}
              <span style={{ color: "#A78BFA" }}>Sohan Bin Ashik.</span>
            </p>
            <p
              className="mt-2 md:mt-3 max-w-md"
              style={{
                fontFamily: "'Karvest',sans-serif",
                fontWeight: 400,
                fontSize: "clamp(11px, 1vw, 16px)",
                lineHeight: 1.5,
                letterSpacing: "0.005em",
                color: "rgba(255,255,255,0.8)",
              }}
            >
              We help creators improve video packaging through thumbnail
              strategy, concept development and design.
            </p>
          </div>

          {/* Founder image — RIGHT on mobile & desktop; hugs the right edge */}
          <div className="reveal flex justify-end -mr-5 md:mr-0">
            <img
              src="/assets/brand/founder-with-shape.png"
              alt="Sohan Bin Ashik"
              className="w-full max-w-[210px] md:max-w-[440px] h-auto"
              style={{
                filter: "drop-shadow(0 22px 60px rgba(79,70,229,0.35))",
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export function LetsPackage() {
  return (
    <section id="lets-package" className="relative py-6 md:py-14">
      <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
        <p
          className="reveal"
          style={{
            fontFamily: "'Karvest',sans-serif",
            fontWeight: 400,
            fontSize: "clamp(18px, 1.8vw, 28px)",
            lineHeight: 1.2,
            letterSpacing: "0.005em",
            color: "rgba(255,255,255,0.85)",
          }}
        >
          Let&apos;s Package Your
        </p>
        <h3
          className="reveal text-transparent bg-clip-text mt-1"
          style={{
            fontFamily: "'Clash Display',sans-serif",
            fontWeight: 500,
            fontSize: "clamp(54px, 8vw, 92px)",
            lineHeight: 0.95,
            letterSpacing: "-0.02em",
            backgroundImage: HEADING_GRADIENT,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            margin: 0,
          }}
        >
          Next Video
        </h3>
        <p
          className="reveal mt-4"
          style={{
            fontFamily: "'Karvest',sans-serif",
            fontWeight: 400,
            fontSize: "clamp(13px, 1vw, 17px)",
            lineHeight: 1.5,
            letterSpacing: "0.005em",
            color: "rgba(255,255,255,0.8)",
          }}
        >
          Whether you already have a concept
          <br />
          or need help developing one, we&apos;d love to help.
        </p>
        <div className="reveal mt-8">
          <a
            href={LINKS.bookProject}
            target="_blank"
            rel="noreferrer"
            className="btn-primary"
          >
            <ArrowUpRight className="w-4 h-4 text-[#8B5CF6]" />
            Book a Project
          </a>
        </div>
      </div>
    </section>
  );
}

// Small reusable link row for socials in Let's Connect
function ContactLink({ item }) {
  return (
    <a
      href={item.href}
      target="_blank"
      rel="noreferrer"
      className="reveal flex items-center gap-4 px-5 py-3 rounded-full bg-white/5 border border-white/10 hover:border-[#8B5CF6]/50 hover:bg-white/10 transition"
    >
      <span className="w-9 h-9 rounded-full bg-[#4F46E5]/40 border border-white/10 flex items-center justify-center shrink-0">
        <item.icon className="w-4 h-4 text-white" />
      </span>
      <span
        className="text-white/90 min-w-0"
        style={{
          fontFamily: "'SF Pro Display',sans-serif",
          fontWeight: 500,
          fontSize: "clamp(11px, 1vw, 15px)",
          letterSpacing: "-0.005em",
          lineHeight: 1.2,
          wordBreak: "break-all",
        }}
      >
        {item.label}
      </span>
    </a>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export function LetsConnect() {
  // Layout matches the Photoshop reference:
  //   LEFT column  → Instagram, X, LinkedIn
  //   RIGHT column → Facebook, Email     (WhatsApp removed per user)
  const leftItems = [
    { icon: Instagram, label: "@clickoryhq", href: LINKS.instagram },
    { icon: XIcon, label: "@clickoryhq", href: LINKS.twitter },
    { icon: Linkedin, label: "Clickory", href: LINKS.linkedin },
  ];
  const rightItems = [
    { icon: Facebook, label: "Clickory", href: LINKS.facebook },
    {
      icon: Mail,
      label: (
        <>
          {/* Mobile: force line break at "@" so it reads:
                hello.clickory@
                gmail.com */}
          <span className="md:hidden">
            hello.clickory@<br />gmail.com
          </span>
          <span className="hidden md:inline">hello.clickory@gmail.com</span>
        </>
      ),
      href: `mailto:${LINKS.email}`,
    },
  ];

  return (
    <section id="contact" className="relative py-6 md:py-14">
      <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
        <h2
          className="reveal text-transparent bg-clip-text"
          style={{
            fontFamily: "'Clash Display',sans-serif",
            fontWeight: 500,
            fontSize: "clamp(52px, 8vw, 92px)",
            lineHeight: 0.95,
            letterSpacing: "-0.02em",
            backgroundImage: HEADING_GRADIENT,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            margin: 0,
          }}
        >
          Let&apos;s Connect
        </h2>
        <p
          className="reveal mt-3"
          style={{
            fontFamily: "'Karvest',sans-serif",
            fontWeight: 400,
            fontSize: "clamp(13px, 1vw, 17px)",
            lineHeight: 1.5,
            letterSpacing: "0.005em",
            color: "rgba(255,255,255,0.8)",
          }}
        >
          Ready to package your next video? Let&apos;s talk.
        </p>

        {/* 3+2 layout — left col has 3, right col has 2 */}
        <div className="mt-10 grid grid-cols-2 gap-3 md:gap-4 max-w-2xl mx-auto">
          <div className="space-y-3 md:space-y-4">
            {leftItems.map((s, i) => (
              <ContactLink key={`l-${i}`} item={s} />
            ))}
          </div>
          <div className="space-y-3 md:space-y-4">
            {rightItems.map((s, i) => (
              <ContactLink key={`r-${i}`} item={s} />
            ))}
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/assets/brand/clickory-logo-full.png"
              alt="Clickory"
              data-testid="footer-logo"
              className="h-9 w-auto"
            />
            <span
              className="text-white/70"
              style={{
                fontFamily: "'SF Pro Display',sans-serif",
                fontWeight: 500,
                fontSize: "13px",
              }}
            >
              clickory.site
            </span>
          </div>
          <div
            className="text-white/50"
            style={{
              fontFamily: "'SF Pro Display',sans-serif",
              fontWeight: 400,
              fontSize: "13px",
            }}
          >
            © {new Date().getFullYear()} Clickory. All rights reserved.
          </div>
        </div>
      </div>
    </section>
  );
}
